import * as Sentry from '@sentry/react';

import { sentrySamplingPolicy } from '@assay/shared/sentrySampling';

type InitSentryParams = {
  dsn?: string;
  environment?: string;
  tracesSampleRate?: number;
};

let initialized = false;

export function _resetSentryForTesting() {
  initialized = false;
}

export function initSentry({ dsn, environment, tracesSampleRate }: InitSentryParams) {
  if (!dsn || initialized) {
    return;
  }

  // Sampling is environment-driven: outside production everything drops to 1%.
  // See packages/shared/src/sentrySampling.ts.
  const sampling = sentrySamplingPolicy({ environment, tracesSampleRate });

  Sentry.init({
    dsn,
    environment,
    sampleRate: sampling.sampleRate,
    tracesSampleRate: sampling.tracesSampleRate,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      ...(sampling.enableLogs
        ? [Sentry.consoleLoggingIntegration({ levels: ['log', 'info', 'warn', 'error'] })]
        : []),
      Sentry.feedbackIntegration({
        colorScheme: 'system',
        showBranding: false,
        triggerLabel: 'Feedback',
        formTitle: 'Send us your feedback',
        submitButtonLabel: 'Send',
        messagePlaceholder: 'Describe the issue or share your thoughts...',
        themeDark: {
          background: '#221e14',
          backgroundHover: '#2a2518',
          foreground: '#f0ead8',
          border: '1px solid rgba(196, 152, 58, 0.12)',
          borderRadius: '2px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(196,152,58,0.12)',
          fontFamily: '"DM Mono", "Menlo", "Monaco", "Consolas", monospace',
          inputBackground: '#0c0906',
          inputBorder: '1px solid rgba(196, 152, 58, 0.12)',
          inputBorderFocus: '1px solid rgba(196, 152, 58, 0.45)',
          inputForeground: '#f0ead8',
          accentBackground: '#c4983a',
          accentForeground: '#0c0906',
          submitBackground: '#c4983a',
          submitBackgroundHover: '#e8b84b',
          submitBorder: 'transparent',
          submitForeground: '#0c0906',
          cancelBackground: 'transparent',
          cancelBackgroundHover: 'rgba(196, 152, 58, 0.08)',
          cancelBorder: 'rgba(196, 152, 58, 0.3)',
          cancelForeground: '#e8b84b',
          errorColor: '#c0392b',
          successColor: '#3a8c80',
        },
        themeLight: {
          background: '#ece5cc',
          backgroundHover: '#e3d9b8',
          foreground: '#1c1508',
          border: '1px solid rgba(196, 152, 58, 0.22)',
          borderRadius: '2px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15), 0 0 0 1px rgba(196,152,58,0.2)',
          fontFamily: '"DM Mono", "Menlo", "Monaco", "Consolas", monospace',
          inputBackground: '#faf7ed',
          inputBorder: '1px solid rgba(196, 152, 58, 0.22)',
          inputBorderFocus: '1px solid rgba(196, 152, 58, 0.45)',
          inputForeground: '#1c1508',
          accentBackground: '#c4983a',
          accentForeground: '#1c1508',
          submitBackground: '#c4983a',
          submitBackgroundHover: '#e8b84b',
          submitBorder: 'transparent',
          submitForeground: '#1c1508',
          cancelBackground: 'transparent',
          cancelBackgroundHover: 'rgba(196, 152, 58, 0.08)',
          cancelBorder: 'rgba(196, 152, 58, 0.3)',
          cancelForeground: '#9a7426',
          errorColor: '#c0392b',
          successColor: '#3a8c80',
        },
      }),
    ],

    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/runassy\.app\/api/],
    // Session Replay - 1% of sessions and 100% of errored sessions in
    // production; effectively off everywhere else.
    replaysSessionSampleRate: sampling.replaysSessionSampleRate,
    replaysOnErrorSampleRate: sampling.replaysOnErrorSampleRate,
    enableLogs: sampling.enableLogs,
  });

  initialized = true;
}
